export const PRODUCT_DOCUMENT_TYPE_ID = 'product';

export type PdfDocumentTemplateStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';

export type LocalizedString = { [locale: string]: string };

export interface PdfDocumentType {
  key: string;
  displayName: string;
  description: string;
  variableDocumentation: string;
  sampleDataDescription: string;
}

export interface PdfDocumentTemplateEditableFields {
  documentTypeId: string;
  name: string;
  description: string;
  htmlTemplate: string;
}

export interface PdfDocumentTemplateGroupSettings {
  displayName?: LocalizedString;
  fileNamePattern?: string;
  allowedRoles?: string[];
  allowedUserGroups?: string[];
  allowedFlows?: string[];
}

export interface PdfDocumentTemplate
  extends PdfDocumentTemplateEditableFields,
    PdfDocumentTemplateGroupSettings {
  id: string;
  templateGroupId: string;
  version: number;
  latestVersion: boolean;
  status: PdfDocumentTemplateStatus;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
  publishedAt?: string;
  publishedBy?: string;
  archivedAt?: string;
  archivedBy?: string;
}

export interface PdfDocumentTemplateListFilters {
  documentTypeId?: string;
  status?: PdfDocumentTemplateStatus;
  latestOnly?: boolean;
}

export interface CatalogueGeneratorResultResponse {
  type: 'result';
  html: string;
  pdfUrl: string;
  summary: string;
}

export interface CatalogueGeneratorQuestionResponse {
  type: 'question';
  question: string;
  reason?: string;
}

export type CatalogueGeneratorResponse = CatalogueGeneratorResultResponse | CatalogueGeneratorQuestionResponse;

export type GenerationJobStatus = 'PENDING' | 'RUNNING' | 'SUCCEEDED' | 'FAILED';

export interface GenerationJobSubmission {
  jobId: string;
  status: GenerationJobStatus;
}

export type GenerationJobPollResponse =
  | { jobId: string; status: 'PENDING' | 'RUNNING' }
  | ({ jobId: string; status: 'SUCCEEDED' } & CatalogueGeneratorResultResponse)
  | ({ jobId: string; status: 'SUCCEEDED' } & CatalogueGeneratorQuestionResponse)
  | { jobId: string; status: 'FAILED'; error?: string };

export interface CatalogueGeneratorHistoryMessage {
  role?: string;
  sender?: string;
  author?: string;
  type?: string;
  kind?: string;
  content?: string;
  text?: string;
  message?: string;
  question?: string;
  reason?: string;
  summary?: string;
  pdfUrl?: string;
  html?: string;
}

export type CatalogueGeneratorHistoryResponse =
  | CatalogueGeneratorHistoryMessage[]
  | {
      messages?: CatalogueGeneratorHistoryMessage[];
      history?: CatalogueGeneratorHistoryMessage[];
      conversation?: CatalogueGeneratorHistoryMessage[];
    };

export type PdfDocumentBuilderAttachmentStatus = 'uploading' | 'ready' | 'error';

export interface PdfDocumentBuilderAttachmentRequest {
  url: string;
  mimeType: string;
  filename?: string;
}

export interface PdfDocumentBuilderAttachmentDraft {
  localId: number;
  file: File;
  mimeType: string;
  filename: string;
  status: PdfDocumentBuilderAttachmentStatus;
  s3Key?: string;
  url?: string;
  error?: string;
}

export const PDF_DOCUMENT_BUILDER_SUPPORTED_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/gif',
  'application/pdf',
  'text/plain',
  'text/markdown',
  'text/x-markdown'
];

export const PDF_DOCUMENT_BUILDER_ATTACHMENT_ACCEPT =
  PDF_DOCUMENT_BUILDER_SUPPORTED_MIME_TYPES.join(',') + ',.md,.markdown';

export const PDF_DOCUMENT_BUILDER_MAX_ATTACHMENTS = 10;

export const PDF_DOCUMENT_BUILDER_MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;
